import React, { useMemo } from "react";
import { Typography } from "@mui/material";
import ConnectionAccordionSection from "../../../../components/view/sections/ConnectionAccordionSection";
import EntityReferenceComponent from "../../../../components/entitiesReferences/EntityReferenceComponent";
import { ViewAccordionField } from "../../../../components/view/fields/ViewAccordion";
import { RichTextEditor } from "../../../../components/field/richTextEditor/RichTextEditor";
import useAncillaryQuery from "../../../../hooks/query/connections/useAncillaryQuery";
import {
  AncillaryConnection,
  SYSTEM_ANCILLARY_TYPE_LABELS,
  SystemAncillaryType,
} from "../../../../types/ancillary";
import { Work } from "../../../../types/work";

interface WorkAncillaryViewSectionProps {
  work: Work;
}

const formatAncillaryType = (type: SystemAncillaryType | null): string =>
  (type && SYSTEM_ANCILLARY_TYPE_LABELS[type]) || "N/A";

const getAncillaryFields = (
  edge: AncillaryConnection
): ViewAccordionField[] => [
  {
    label: "Ancillary Type",
    value: (
      <Typography variant="body1">
        {formatAncillaryType(edge?.work_ancillary_type ?? null)}
      </Typography>
    ),
  },
  {
    label: "Notes",
    value: (
      <RichTextEditor
        value={typeof edge?.notes === "string" ? edge.notes : ""}
        readOnly
        fullWidth
        placeholder="N/A"
      />
    ),
  },
];

const WorkAncillaryViewSection: React.FC<WorkAncillaryViewSectionProps> = ({
  work,
}) => {
  const { useFetchConnectedEdges } = useAncillaryQuery();
  const { data, isLoading, errorMessage } = useFetchConnectedEdges(
    work?._id,
    "outbound",
    {}
  );

  const items = useMemo<AncillaryConnection[]>(() => data?.data ?? [], [data]);

  return (
    <ConnectionAccordionSection<AncillaryConnection>
      title="Ancillary"
      loading={isLoading}
      error={errorMessage}
      items={items}
      getItemId={(edge, index) => edge?._key ?? `ancillary-${index}`}
      getItemTitle={(edge) =>
        edge?.toDoc ? (
          <EntityReferenceComponent entity={edge.toDoc} linkMode="view" />
        ) : (
          <Typography variant="body1">N/A</Typography>
        )
      }
      getItemSubtitle={(edge) =>
        formatAncillaryType(edge?.work_ancillary_type ?? null)
      }
      getItemFields={(edge) => getAncillaryFields(edge)}
      emptyText="No ancillary works"
    />
  );
};

export default WorkAncillaryViewSection;
